import {Component, Input, ViewEncapsulation, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, shareReplay, startWith, switchMap, takeWhile} from 'rxjs/operators';

import {OrderItemDelivery} from '../../../../models/order-item-delivery.model';
import {OrderItem} from '../../../../models/order-item.model';
import {ProductService} from '../../../../services/product.service';
import {AllocationGroup} from '../../../../interfaces/allocation-group';
import {isEmptyArray} from '../../../../shared/utils';

// This  component  should be user  only  for BW  products with  enabled allocated inventory

@Component({
    selector: 'app-allocation-group-control',
    templateUrl: './allocation-group-control.component.html',
    styleUrls: ['./allocation-group-control.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class AllocationGroupControlComponent  implements  OnInit, OnDestroy {

  @Input() orderItem: OrderItem;
  @Input() readonly = false;

  @Output() onSelectAllocationGroupId =  new EventEmitter<number>();

  searchSubject: Subject<string> = new Subject<string>();
  filteredAllocationGroups$: Observable<AllocationGroup[]>;
  allocationGroups$: Observable<AllocationGroup[]>;

  // Convert delivery to setter/getter to trigger re-evaluation when it changes
  private _delivery: OrderItemDelivery;
  private deliveryChange$ = new BehaviorSubject<void>(undefined);
  private _cachedAllocationGroups$: Observable<AllocationGroup[]> | null = null;
  private isAlive = true;

  @Input()
  set delivery(value: OrderItemDelivery) {
    this._delivery = value;
    this.deliveryChange$.next();
  }

  get delivery(): OrderItemDelivery {
    return this._delivery;
  }

  constructor(private productService: ProductService) {
  }

  ngOnInit() {
    // Make allocationGroups$ reactive to delivery changes
    this.allocationGroups$ = this.deliveryChange$.pipe(
      switchMap(() => this.getAllocationGroups$())
    );

    // search by label in dropdown
    this.filteredAllocationGroups$ = this.searchSubject
      .pipe(
        takeWhile(() => this.isAlive),
        debounceTime(200),
        distinctUntilChanged(),
        startWith(''),
        switchMap(term => {
          if (!term) {
            return this.allocationGroups$;
          }
          return this.allocationGroups$.pipe(
            map(groups => groups.filter(g =>
              g.allocation_group_label.toLowerCase().includes(term.toLowerCase())
            ))
          );
        })
      );
  }

  ngOnDestroy() {
    this.isAlive = false;
  }


  get enabled(): boolean {
    return this.orderItem?.product.isEnabledAllocatedInventory
      && this.isWarehouseAddressIsSelected;
  }

  protected onSelect(group: AllocationGroup) {
    if (!group) {
      return;
    }
    this.onSelectAllocationGroupId.emit(group.allocation_group_id);
  }


  get isWarehouseAddressIsSelected(): boolean {
    return this.delivery?.getAddress()?.isWarehouse === true;
  }

  get isBuyingWindow(): boolean {
    return this.orderItem?.product.isBuyingWindow;
  }


  private getAllocationGroups$(): Observable<AllocationGroup[]> {
    if (!this.enabled) {
      return of([]);
    }

    if (!this._cachedAllocationGroups$) {
      this._cachedAllocationGroups$ = this.productService.fetchAllocationGroups().pipe(
        shareReplay(1)
      );
    }
    return this._cachedAllocationGroups$;
  }

  get someDeliveriesHaveAllocationGroup(): boolean {
    if (isEmptyArray(this.orderItem?.deliveries)) {
      return false;
    }

    if (!this.readonly) {
      return this.orderItem?.product.isEnabledAllocatedInventory
        && this.orderItem?.deliveries.some(d => d.getAddress().isWarehouse === true);
    }  else {
      return this.orderItem?.product.isEnabledAllocatedInventory
        && this.orderItem?.deliveries.some(d => !!d.allocation_group_id);
    }
  }

  get hasSelectedAllocationGroup(): boolean {
    return !!this.delivery?.allocation_group_id;
  }


  get isHidden(): boolean {
    if (this.readonly) {
      return !this.someDeliveriesHaveAllocationGroup;
    } else {
      return !this.enabled;
    }
  }

}
